import { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddOutlined from "@mui/icons-material/AddOutlined";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import { DataContext } from "../../contexts/DataProvider";
import { useAlert } from "../../functions/useAlert";
import {
  fetchAgentModelOptions,
  fetchAgentModelCatalogue,
  fetchAgentModelSettings,
  updateAgentModelProvider,
  updateAgentModelSettings,
} from "../../services/organisations.service";

type ProviderName = "azure-openai" | "openai" | "anthropic" | "gemini";

interface ProviderOption {
  provider: ProviderName;
  label: string;
  configured: boolean;
  models: string[];
}

interface ModelSettings {
  provider: ProviderName;
  model: string;
  fallbackProvider?: ProviderName;
  fallbackModel?: string;
}

interface OptionsResponse { success: boolean; providers: ProviderOption[] }
interface SettingsResponse { success: boolean; settings: ModelSettings }
interface ModelDefinition {
  id: string;
  displayName?: string;
  deploymentName?: string;
  modelVersion?: string;
  apiVersion?: string;
  enabled: boolean;
}
interface ProviderConfiguration {
  provider: ProviderName;
  label: string;
  enabled: boolean;
  apiKeySecretRef?: string;
  endpointSecretRef?: string;
  models: ModelDefinition[];
}
interface CatalogueResponse { success: boolean; providers: ProviderConfiguration[] }

const EMPTY_MODEL = {
  provider: "azure-openai" as ProviderName,
  displayName: "",
  deploymentName: "",
  modelVersion: "",
  apiVersion: "",
};

export default function AgentModelSettingsCard({ orgId }: { orgId?: string }) {
  const { login } = useContext(DataContext);
  const [setAlert] = useAlert();
  const effectiveOrgId = login?.data?.role === "global admin" ? orgId : login?.data?.orgId;
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const [settings, setSettings] = useState<ModelSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [modelForm, setModelForm] = useState(EMPTY_MODEL);
  const [catalogue, setCatalogue] = useState<ProviderConfiguration[]>([]);
  const [savingModel, setSavingModel] = useState(false);
  const isGlobalAdmin = login?.data?.role === "global admin";

  useEffect(() => {
    if (!effectiveOrgId || effectiveOrgId === "All Organisation") {
      setLoading(false);
      return;
    }
    let active = true;
    Promise.all([
      fetchAgentModelOptions() as Promise<OptionsResponse>,
      fetchAgentModelSettings(effectiveOrgId) as Promise<SettingsResponse>,
    ])
      .then(([optionsResponse, settingsResponse]) => {
        if (!active) return;
        setProviders(optionsResponse.providers || []);
        setSettings(settingsResponse.settings);
      })
      .catch(() => setAlert("Could not load agent model settings", "error"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [effectiveOrgId, setAlert]);

  const primary = useMemo(
    () => providers.find((option) => option.provider === settings?.provider),
    [providers, settings?.provider],
  );
  const fallback = useMemo(
    () => providers.find((option) => option.provider === settings?.fallbackProvider),
    [providers, settings?.fallbackProvider],
  );
  const selectableProviders = useMemo(
    () => providers.filter((option) => option.configured && option.models.length > 0),
    [providers],
  );

  if (!effectiveOrgId || effectiveOrgId === "All Organisation") return null;

  const selectProvider = (provider: ProviderName) => {
    const option = providers.find((candidate) => candidate.provider === provider);
    setSettings((current) => current && ({ ...current, provider, model: option?.models[0] || "" }));
  };

  const selectFallback = (provider: "" | ProviderName) => {
    const option = providers.find((candidate) => candidate.provider === provider);
    setSettings((current) => current && (provider
      ? { ...current, fallbackProvider: provider, fallbackModel: option?.models[0] || "" }
      : { provider: current.provider, model: current.model }));
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await updateAgentModelSettings(effectiveOrgId, settings as unknown as Record<string, unknown>);
      setAlert("Agent model settings updated", "success");
    } catch (error: any) {
      setAlert(error?.message || "Could not update agent model settings", "error");
    } finally {
      setSaving(false);
    }
  };

  const openAddModel = async () => {
    setModelForm(EMPTY_MODEL);
    setModelDialogOpen(true);
    try {
      const response = await fetchAgentModelCatalogue() as CatalogueResponse;
      setCatalogue(response.providers || []);
    } catch (error: any) {
      setModelDialogOpen(false);
      setAlert(error?.message || "Could not load the model catalogue", "error");
    }
  };

  const addModel = async () => {
    const deploymentName = modelForm.deploymentName.trim();
    const displayName = modelForm.displayName.trim();
    const apiVersion = modelForm.apiVersion.trim();
    if (!deploymentName || !displayName || !apiVersion) {
      setAlert("Model name, deployment name, and API version are required", "error");
      return;
    }
    const existing = catalogue.find((item) => item.provider === modelForm.provider);
    const option = providers.find((item) => item.provider === modelForm.provider);
    const currentModels = existing?.models || (option?.models || []).map((id) => ({
      id,
      deploymentName: id,
      enabled: true,
    }));
    if (currentModels.some((model) => model.id === displayName || model.deploymentName === deploymentName)) {
      setAlert("That model name or deployment already exists for this provider", "error");
      return;
    }
    setSavingModel(true);
    try {
      await updateAgentModelProvider(modelForm.provider, {
        label: existing?.label || option?.label || modelForm.provider,
        enabled: existing?.enabled !== false,
        apiKeySecretRef: existing?.apiKeySecretRef,
        endpointSecretRef: existing?.endpointSecretRef,
        models: [
          ...currentModels,
          {
            // The user-facing name is the stable catalogue id selected by an
            // organisation; runtime resolution maps it to deploymentName.
            id: displayName,
            displayName,
            deploymentName,
            ...(modelForm.modelVersion.trim() ? { modelVersion: modelForm.modelVersion.trim() } : {}),
            apiVersion,
            enabled: true,
          },
        ],
      });
      const optionsResponse = await fetchAgentModelOptions() as OptionsResponse;
      setProviders(optionsResponse.providers || []);
      setModelDialogOpen(false);
      setAlert(`${displayName} added to the model catalogue`, "success");
    } catch (error: any) {
      setAlert(error?.message || "Could not add the model", "error");
    } finally {
      setSavingModel(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SmartToyOutlined color="primary" />
            <Typography variant="h6" component="h2">Prospo Agent Model</Typography>
          </Box>
          {isGlobalAdmin && (
            <Button variant="outlined" size="small" startIcon={<AddOutlined />} onClick={openAddModel}>
              Add model
            </Button>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Change the model used by the Prospo Agent without redeploying. Credentials remain in server configuration or Key Vault.
        </Typography>
        {loading || !settings ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Provider</InputLabel>
                  <Select label="Provider" value={settings.provider} onChange={(event) => selectProvider(event.target.value as ProviderName)}>
                    {selectableProviders.map((option) => (
                      <MenuItem key={option.provider} value={option.provider}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Model / deployment</InputLabel>
                  <Select label="Model / deployment" value={settings.model} onChange={(event) => setSettings({ ...settings, model: event.target.value })}>
                    {(primary?.models || []).map((model) => <MenuItem key={model} value={model}>{model}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Fallback provider</InputLabel>
                  <Select label="Fallback provider" value={settings.fallbackProvider || ""} onChange={(event) => selectFallback(event.target.value as "" | ProviderName)}>
                    <MenuItem value="">None</MenuItem>
                    {selectableProviders.map((option) => (
                      <MenuItem key={option.provider} value={option.provider}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!settings.fallbackProvider}>
                  <InputLabel>Fallback model</InputLabel>
                  <Select label="Fallback model" value={settings.fallbackModel || ""} onChange={(event) => setSettings({ ...settings, fallbackModel: event.target.value })}>
                    {(fallback?.models || []).map((model) => <MenuItem key={model} value={model}>{model}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {!selectableProviders.length && <Alert severity="warning" sx={{ mt: 2 }}>No configured agent models are available.</Alert>}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="contained" onClick={save} disabled={saving || !settings.model}>
                {saving ? <CircularProgress size={20} color="inherit" /> : "Save agent model"}
              </Button>
            </Box>
          </>
        )}
      </CardContent>
      <Dialog open={modelDialogOpen} onClose={() => !savingModel && setModelDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add agent model</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add safe model metadata to MongoDB. API keys and endpoint URLs remain in server configuration or Key Vault.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Provider</InputLabel>
                <Select
                  label="Provider"
                  value={modelForm.provider}
                  onChange={(event) => setModelForm((current) => ({
                    ...current,
                    provider: event.target.value as ProviderName,
                    apiVersion: event.target.value === "anthropic" ? "2023-06-01" : current.apiVersion,
                  }))}
                >
                  <MenuItem value="azure-openai">Azure OpenAI</MenuItem>
                  <MenuItem value="anthropic">Anthropic</MenuItem>
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="gemini">Google Gemini</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth required label="Model name" value={modelForm.displayName} onChange={(event) => setModelForm((current) => ({ ...current, displayName: event.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth required label="Deployment name" value={modelForm.deploymentName} onChange={(event) => setModelForm((current) => ({ ...current, deploymentName: event.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Azure model version" placeholder="For example, 2" value={modelForm.modelVersion} onChange={(event) => setModelForm((current) => ({ ...current, modelVersion: event.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label={modelForm.provider === "anthropic" ? "Anthropic protocol version" : "API version"}
                placeholder={modelForm.provider === "anthropic" ? "2023-06-01" : "2025-04-01-preview"}
                value={modelForm.apiVersion}
                onChange={(event) => setModelForm((current) => ({ ...current, apiVersion: event.target.value }))}
                helperText={modelForm.provider === "anthropic" ? "Azure model version 2 is separate from this protocol header." : undefined}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModelDialogOpen(false)} disabled={savingModel}>Cancel</Button>
          <Button variant="contained" onClick={addModel} disabled={savingModel}>
            {savingModel ? <CircularProgress size={20} color="inherit" /> : "Add model"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
